import { useMemo, useRef } from "react";
import { Icon } from "@/components/icon/Icon";
import { LogoLoop, type LogoLoopItem } from "@/components/motion/LogoLoop";
import { livePortfolio } from "@/data/portfolio";
import { HERO_BADGES } from "@/data/process";
import { gsap, MOTION_OK, useGSAP } from "@/lib/gsap";
import { usePortfolio } from "@/lib/portfolio";

/**
 * Full-viewport hero with text-based kinetic typography over a token-driven
 * animated gradient mesh (no video). Left-aligned for a directional read order.
 * Respects prefers-reduced-motion: everything renders at its final state, and
 * the background stops drifting.
 *
 * 애니메이션은 전부 하나의 GSAP 타임라인이에요 — 줄마다 delay 를 손으로 더하면
 * 문구가 늘 때마다 숫자를 다시 맞춰야 하지만, 타임라인은 stagger 가 대신 해줘요.
 * 타깃은 `data-hero` 속성으로 잡아요(스타일 클래스에 애니메이션을 얹으면 클래스명을
 * 바꾸는 순간 조용히 안 움직여요). 선택자는 `scope` 안으로 한정돼요.
 */
export function Hero() {
  const root = useRef<HTMLElement>(null);
  const portfolio = usePortfolio();
  // 운영 중인 서비스 목록은 포트폴리오 데이터가 원본 — 여기서 이름을 또 적지 않아요.
  const runningServices = useMemo<LogoLoopItem[]>(
    () =>
      livePortfolio(portfolio)
        .filter((item) => item.logo)
        .map((item) => ({
          label: item.name,
          node: (
            <span className="flex items-center gap-2">
              <img
                src={item.logo}
                alt=""
                width="28"
                height="28"
                className="size-7 rounded-md object-contain"
              />
              <span className="wds-label-1 whitespace-nowrap text-static-white">{item.name}</span>
            </span>
          ),
        })),
    [portfolio]
  );

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      // 동작 줄이기가 켜져 있으면 아무것도 걸지 않아요 — 마크업이 곧 최종 상태예요.
      mm.add(MOTION_OK, () => {
        const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
        tl.from('[data-hero="line"]', { yPercent: 115, duration: 0.7, stagger: 0.09 }, 0.1)
          .from('[data-hero="underline"]', { scaleX: 0, duration: 0.6 }, 0.55)
          .from('[data-hero="rise"]', { opacity: 0, y: 14, duration: 0.5, stagger: 0.07 }, 0.25);

        // 배경 메시는 끝없이 아주 느리게 흘러요. yoyo 라 되감기는 순간 튀지 않아요.
        gsap.to('[data-hero="mesh"]', {
          backgroundPosition: "100% 100%",
          duration: 24,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
        });

        // 스크롤 커서에 매인 패럴랙스 — 내용이 배경보다 빨리 밀려 올라가며 흐려져요.
        gsap.to('[data-hero="content"]', {
          yPercent: -14,
          opacity: 0,
          ease: "none",
          scrollTrigger: { trigger: root.current, start: "top top", end: "bottom top", scrub: 0.4 },
        });

        // 스크롤 유도. 히어로를 벗어나면 사라져요.
        gsap.to('[data-hero="cue"]', {
          y: 8,
          duration: 1,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
        });
        gsap.to('[data-hero="cue"]', {
          opacity: 0,
          ease: "none",
          scrollTrigger: { trigger: root.current, start: "top top", end: "+=200", scrub: true },
        });
      });
    },
    { scope: root }
  );

  return (
    <section ref={root} className="relative flex min-h-screen items-center overflow-hidden">
      {/* Token-driven gradient mesh background (no raw hex — CSS vars only). */}
      <div
        data-hero="mesh"
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundColor: "var(--primary-strong)",
          backgroundImage:
            "radial-gradient(60% 60% at 18% 22%, var(--primary-normal) 0%, transparent 60%)," +
            "radial-gradient(55% 55% at 82% 30%, var(--primary-heavy) 0%, transparent 55%)," +
            "radial-gradient(70% 70% at 60% 90%, var(--background-normal) 0%, transparent 65%)",
          backgroundSize: "160% 160%",
        }}
      />
      {/* Left-side scrim keeps white text legible over the lighter gradient stops. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-r from-static-black to-transparent opacity-50"
      />

      <div
        data-hero="content"
        className="relative z-10 mx-auto flex w-full max-w-layout flex-col items-start gap-7 px-6 text-left"
      >
        {/* 반투명 알약. 토큰 색에 알파를 얹어야 해서 Tailwind 슬래시 대신 color-mix 를 써요
            (프리셋 색은 <alpha-value> 없는 var() 라 `bg-static-white/10` 이 안 먹어요). */}
        <p
          data-hero="rise"
          className="wds-label-1 flex items-center gap-2 rounded-full border px-3 py-1 text-static-white backdrop-blur-sm"
          style={{
            borderColor: "color-mix(in srgb, var(--static-white) 32%, transparent)",
            backgroundColor: "color-mix(in srgb, var(--static-white) 12%, transparent)",
          }}
        >
          <Icon name="thunderFill" size={14} aria-hidden="true" />
          네이버·카이스트 출신 웹·앱 개발팀
        </p>

        {/* 각 줄은 nowrap — 크기는 --font-size-display-2 의 clamp() 가 뷰포트에 맞춰 줄여요. */}
        <h1 className="wds-display-2 text-static-white">
          <span className="block overflow-hidden pb-1">
            <span data-hero="line" className="block whitespace-nowrap">
              아이디어만 주시면,
            </span>
          </span>
          <span className="block overflow-hidden pb-1">
            <span data-hero="line" className="block whitespace-nowrap">
              <span className="relative inline-block">
                서비스로
                <span
                  data-hero="underline"
                  aria-hidden="true"
                  className="absolute bottom-0 left-0 h-1 w-full origin-left rounded-full bg-static-white"
                />
              </span>{" "}
              만들어 드려요
            </span>
          </span>
        </h1>

        {/* 문장마다 줄을 나눠요. 폭은 긴 쪽 문장이 한 줄에 들어가게 잡았고, 좁은 화면에서는 자연스럽게 접혀요. */}
        <p data-hero="rise" className="wds-body-1-reading max-w-2xl text-static-white opacity-90">
          <span className="block">직접 만들어 운영해본 팀이라, 만들고 끝내지 않아요.</span>
          <span className="block">기획부터 개발·배포·운영까지 한 팀이 맡아요.</span>
        </p>

        <div data-hero="rise" className="flex flex-wrap items-center gap-4">
          <a
            href="#portfolio"
            className="group inline-flex h-12 items-center justify-center gap-1.5 rounded-md bg-static-white px-8 wds-body-1 font-semibold text-primary shadow-normal transition-colors duration-200 ease-standard hover:bg-background-alternative"
          >
            포트폴리오 보기
            <Icon
              name="arrowRight"
              size={18}
              aria-hidden="true"
              className="transition-transform duration-200 ease-standard group-hover:translate-x-1"
            />
          </a>
          <a
            href="#quote"
            className="inline-flex h-12 items-center justify-center rounded-md border border-static-white px-8 wds-body-1 font-semibold text-static-white transition-colors duration-200 ease-standard hover:bg-static-white hover:text-primary"
          >
            견적 계산하기
          </a>
        </div>

        {/* 받으시는 것 — 넓으면 한 줄, 좁으면 항목마다 줄을 나눠요(2+1 로 어긋나게 접히지 않게). */}
        <div data-hero="rise">
          <ul className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-5">
            {HERO_BADGES.map((badge) => (
              <li key={badge} className="flex items-center gap-1.5">
                <Icon name="circleCheckFill" size={16} className="shrink-0 text-static-white" />
                <span className="wds-label-1 font-semibold text-static-white">{badge}</span>
              </li>
            ))}
          </ul>
        </div>

        <div data-hero="rise" className="flex w-full flex-col gap-3">
          <p className="wds-caption-1 text-static-white opacity-70">
            직접 만들어 지금도 운영 중인 서비스예요
          </p>
          {/* 목록을 받아오기 전에는 빈 목록이에요(`usePortfolio` 주석 참고). 자리를 미리 잡아 둬서
              로고가 도착할 때 아래 내용이 밀리지 않게 해요 — 높이는 로고 크기(size-7)와 같아요. */}
          {runningServices.length > 0 ? (
            <LogoLoop
              items={runningServices}
              ariaLabel="운영 중인 서비스"
              speed={40}
              gap={40}
              className="opacity-90"
            />
          ) : (
            <div aria-hidden="true" className="h-7" />
          )}
        </div>
      </div>

      {/* 아래에 더 있다는 신호. 장식이라 스크린리더에서는 빼요.
          bottom-24 인 건 섹션이 100vh 인데 위에 4rem 짜리 sticky 헤더가 얹혀 있어서예요 —
          bottom-6 으로 두면 섹션 아래끝이 화면 밖이라 화살표도 같이 잘려요. */}
      <span
        data-hero="cue"
        aria-hidden="true"
        className="-translate-x-1/2 absolute bottom-24 left-1/2 z-10 hidden text-static-white opacity-60 sm:block"
      >
        <Icon name="chevronDown" size={22} />
      </span>
    </section>
  );
}

export default Hero;
